<script setup lang="ts">
import { computed, ref, toRef, type ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import ViewerPill from '@/components/bop/ViewerPill.vue'
import { useSelectionPop } from '@/composables/useSelectionPop'
import type { GizmoMode } from '@/composables/useTransformGizmo'

const props = defineProps<{
  selected: boolean
  selectionKey: string | null
  title: string
  instanceIndex: number | null
  instanceTotal: number
  canTransform: boolean
  activeGizmo: GizmoMode | null
  canAllocate: boolean
  needsInstanceSelection: boolean
  allocated: boolean
  canAllocateAllInstances: boolean
}>()

const emit = defineEmits<{
  gizmo: [mode: GizmoMode | null]
  'pick-instances': []
  allocate: []
  'allocate-all-instances': []
  zoom: []
  clear: []
}>()

const { t } = useI18n()

const pill = ref<ComponentPublicInstance>()
useSelectionPop(
  toRef(props, 'selectionKey'),
  computed(() => pill.value?.$el as HTMLElement | undefined),
)
</script>

<template>
  <viewer-pill
    ref="pill"
    floating
    class="viewer-selection-bar"
    :class="{ 'viewer-selection-bar--selected': selected }"
  >
    <v-icon size="18" color="grey-darken-2">ads_click</v-icon>
    <span class="viewer-selection-bar__label">
      {{ selected ? title : t('component.bopModelViewer.selectPrompt') }}
    </span>
    <span v-if="canAllocate && instanceIndex !== null" class="viewer-selection-bar__meta">
      {{
        t('component.bopModelViewer.instanceOf', { index: instanceIndex + 1, total: instanceTotal })
      }}
    </span>
    <template v-if="canTransform">
      <v-divider vertical class="mx-1" />
      <v-btn-toggle
        :model-value="activeGizmo"
        mandatory
        density="compact"
        color="primary"
        variant="outlined"
        divided
        @update:model-value="(mode: GizmoMode | null) => emit('gizmo', mode)"
      >
        <v-btn value="translate" size="small" prepend-icon="open_with">
          {{ t('component.bopModelViewer.move') }}
        </v-btn>
        <v-btn value="rotate" size="small" prepend-icon="3d_rotation">
          {{ t('component.bopModelViewer.rotate') }}
        </v-btn>
      </v-btn-toggle>
    </template>
    <v-divider v-if="canAllocate" vertical class="mx-1" />
    <v-btn
      v-if="canAllocate && needsInstanceSelection"
      size="small"
      variant="flat"
      color="primary"
      prepend-icon="touch_app"
      @click="emit('pick-instances')"
    >
      {{ t('component.bopModelViewer.selectInstances') }}
    </v-btn>
    <template v-else-if="canAllocate">
      <v-btn
        v-if="!allocated"
        size="small"
        variant="flat"
        color="primary"
        prepend-icon="add"
        @click="emit('allocate')"
      >
        {{ t('component.bopModelViewer.addToStep') }}
      </v-btn>
      <v-btn
        v-else
        size="small"
        variant="tonal"
        color="error"
        prepend-icon="link_off"
        @click="emit('allocate')"
      >
        {{ t('component.bopModelViewer.removeFromStep') }}
      </v-btn>
    </template>
    <v-btn
      v-if="canAllocateAllInstances"
      size="small"
      variant="tonal"
      color="primary"
      prepend-icon="select_all"
      @click="emit('allocate-all-instances')"
    >
      {{ t('component.bopModelViewer.allocateAllInstances') }}
    </v-btn>
    <template v-if="selected">
      <v-btn
        icon
        size="x-small"
        variant="text"
        color="grey-darken-2"
        :title="t('component.bopModelViewer.zoomToSelection')"
        @click="emit('zoom')"
      >
        <v-icon>zoom_in_map</v-icon>
      </v-btn>
      <v-btn
        icon
        size="x-small"
        variant="text"
        color="grey-darken-2"
        :title="t('component.bopModelViewer.closeSelection')"
        @click="emit('clear')"
      >
        <v-icon>close</v-icon>
      </v-btn>
    </template>
  </viewer-pill>
</template>

<style scoped>
.viewer-selection-bar__label {
  max-width: 200px;
  overflow: hidden;
  font-size: 0.8em;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer-selection-bar__meta {
  font-size: 0.75em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.viewer-selection-bar--selected.viewer-bar-enter-active {
  animation: viewer-selection-fade-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.viewer-selection-bar--selected.viewer-bar-enter-active::before {
  animation: viewer-selection-backdrop-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.viewer-selection-bar--selected.viewer-bar-leave-active {
  animation: viewer-selection-fade-out 140ms cubic-bezier(0.7, 0, 0.84, 0) both;
}

.viewer-selection-bar--selected.viewer-bar-leave-active::before {
  animation: viewer-selection-backdrop-out 140ms cubic-bezier(0.7, 0, 0.84, 0) both;
}

@keyframes viewer-selection-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes viewer-selection-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes viewer-selection-backdrop-in {
  from {
    transform: scale(0.94);
  }
  to {
    transform: none;
  }
}

@keyframes viewer-selection-backdrop-out {
  from {
    transform: none;
  }
  to {
    transform: scale(0.96);
  }
}

@media (prefers-reduced-motion: reduce) {
  .viewer-selection-bar--selected.viewer-bar-enter-active,
  .viewer-selection-bar--selected.viewer-bar-enter-active::before,
  .viewer-selection-bar--selected.viewer-bar-leave-active,
  .viewer-selection-bar--selected.viewer-bar-leave-active::before {
    animation: none;
  }
}
</style>
